import { ChangeEvent } from 'react';
import { FormHelperText, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';

import { CoreFormControl } from '@youscience/core';

import { CourseSelection, SchoolReportRequest, SchoolReportSchema, YesNo } from '@youscience/commonapp-service-common';

import SectionAccordion from '@components/SectionAccordion';
import RadioButtonGroupWithClearButton from '@components/RadioGroupWithClearButton';

import ContinueButton from '@components/ContinueButton';

import { FormState } from '@constants/commonApp/types';
import { StyledFieldsContainer } from '@pages/RecommenderSpace/components/Card.styles';
import { useApplicantFormSection } from '@pages/StudentDetailsPage/hooks/useApplicantFormSection';

export type CurriculumFields = Partial<SchoolReportRequest['curriculum']>;

interface CurriculumProps {
  curriculum?: CurriculumFields;
  onFormChange: (formState: FormState<CurriculumFields>) => void;
  onExpand: () => void;
  onContinue: () => void;
  isExtended: boolean;
  isSaved: boolean;
}

const validator = SchoolReportSchema.shape.curriculum;

export const Curriculum = ({
  curriculum = {},
  onFormChange,
  onExpand,
  onContinue,
  isExtended,
  isSaved,
}: CurriculumProps) => {
  const [formState, setFormStateWithErrors] = useApplicantFormSection<CurriculumFields, typeof validator>(
    curriculum,
    validator,
    isSaved,
    onFormChange,
  );

  const isShowErrors = new URLSearchParams(window.location.search).get('showErrors') === 'true';

  const handleSelectChange = (field: string) => (event: SelectChangeEvent<unknown>) => {
    setFormStateWithErrors({ [field]: (event.target.value as string) || '' });
  };

  const handleRadioChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormStateWithErrors({ [field]: event.target.value });
  };

  return (
    <SectionAccordion
      title="Curriculum"
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
      showCheckmark={formState.isSaved && !!formState.isFormValid}
      isError={isShowErrors && !formState.isFormValid}
    >
      <StyledFieldsContainer>
        <CoreFormControl fullWidth error={!!formState.errors.courseSelection}>
          <InputLabel required>
            In comparison with other college preparatory students at your school, the applicant's course selection is
          </InputLabel>

          <Select
            label="In comparison with other college preparatory students at your school, the applicant's course selection is"
            value={formState.data.courseSelection ?? ''}
            onChange={handleSelectChange('courseSelection')}
          >
            {Object.entries(CourseSelection).map(([key, value]) => {
              return (
                <MenuItem key={value} value={value}>
                  {key}
                </MenuItem>
              );
            })}
          </Select>

          {formState.errors.courseSelection && (
            <FormHelperText error>{formState.errors.courseSelection}</FormHelperText>
          )}
        </CoreFormControl>

        <RadioButtonGroupWithClearButton
          name="ibCandidate"
          title="Is the applicant an IB Diploma Candidate?"
          value={formState.data.ibCandidate ?? undefined}
          options={YesNo}
          required
          handleRadioChange={handleRadioChange}
          handleClear={() => setFormStateWithErrors({ ibCandidate: undefined })}
          error={!!formState.errors.ibCandidate}
          helperText={formState.errors.ibCandidate}
        />

        <RadioButtonGroupWithClearButton
          name="aiceCandidate"
          title="Is the applicant an Advanced Cambridge (AICE) Diploma Candidate?"
          value={formState.data.aiceCandidate ?? undefined}
          options={YesNo}
          required
          handleRadioChange={handleRadioChange}
          handleClear={() => setFormStateWithErrors({ aiceCandidate: undefined })}
          error={!!formState.errors.aiceCandidate}
          helperText={formState.errors.aiceCandidate}
        />

        <RadioButtonGroupWithClearButton
          name="apCapstoneCandidate"
          title="Is the applicant an AP Capstone Diploma candidate?"
          value={formState.data.apCapstoneCandidate ?? undefined}
          options={YesNo}
          required
          handleRadioChange={handleRadioChange}
          handleClear={() => setFormStateWithErrors({ apCapstoneCandidate: undefined })}
          error={!!formState.errors.apCapstoneCandidate}
          helperText={formState.errors.apCapstoneCandidate}
        />
        <ContinueButton onContinue={onContinue} />
      </StyledFieldsContainer>
    </SectionAccordion>
  );
};
